import React, { useState } from 'react';
import { Sparkles, ArrowRight, AlertCircle, MailCheck, RefreshCw, ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';

export const LoginView: React.FC = () => {
  const { login, verifyEmail, resendVerificationCode } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email Verification Step
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyEmailAddress, setVerifyEmailAddress] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isResending, setIsResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setIsSubmitting(true);

    const result = await login(email, password);
    if (!result.success) {
      if (result.requiresVerification) {
        setIsVerifying(true);
        setVerifyEmailAddress(result.email || email);
        setVerificationCode('');
        setError(null);
      } else {
        setError(result.error || 'Authentication failed');
      }
    }
    setIsSubmitting(false);
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    if (!verificationCode.trim()) {
      setError('Please enter the 6-digit verification code.');
      return;
    }
    setIsSubmitting(true);

    const result = await verifyEmail(verifyEmailAddress, verificationCode.trim());
    if (!result.success) {
      setError(result.error || 'Verification failed. Please check the code.');
    }
    setIsSubmitting(false);
  };

  const handleResendCode = async () => {
    setError(null);
    setSuccessMsg(null);
    setIsResending(true);
    const result = await resendVerificationCode(verifyEmailAddress);
    if (result.success) {
      setSuccessMsg(
        result.code
          ? `New verification code issued: ${result.code}`
          : 'Verification code has been resent to your email.'
      );
    } else {
      setError(result.error || 'Failed to resend verification code.');
    }
    setIsResending(false);
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-surface-100 via-white to-surface-100 dark:from-surface-950 dark:via-surface-900 dark:to-surface-950">
      <div className="w-full max-w-md bg-white dark:bg-surface-900 rounded-2xl sm:rounded-3xl shadow-modal border border-surface-200 dark:border-surface-800 p-5 sm:p-8">
        {/* Brand Header */}
        <div className="text-center mb-8 flex flex-col items-center">
          <img
            src="/ihb-pulse-primary.png"
            alt="Pulse"
            className="h-12 w-auto object-contain mb-2 dark:hidden"
          />
          <img
            src="/ihb-pulse-white.png"
            alt="Pulse"
            className="h-12 w-auto object-contain mb-2 hidden dark:block"
          />
          <p className="text-xs text-surface-500 font-semibold tracking-wide">
            Project Management & Collaboration
          </p>
        </div>

        {error && (
          <div className="mb-6 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-xs flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="mb-6 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs flex items-center space-x-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {isVerifying ? (
          /* Email Verification Form */
          <form onSubmit={handleVerifySubmit} className="space-y-4">
            <div className="p-4 rounded-2xl bg-brand-50/50 dark:bg-brand-950/30 border border-brand-500/20 text-center">
              <div className="w-10 h-10 rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center mx-auto mb-2">
                <MailCheck className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-surface-900 dark:text-surface-100">
                Email Verification Required
              </h3>
              <p className="text-xs text-surface-600 dark:text-surface-400 mt-1">
                Your account is pending verification. Please enter the 6-digit code issued for:
              </p>
              <div className="mt-1.5 font-mono text-xs font-bold text-brand-600 dark:text-brand-400 bg-white dark:bg-surface-800 py-1 px-2.5 rounded-lg inline-block border border-brand-500/20">
                {verifyEmailAddress}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1.5 text-center">
                6-Digit Verification Code
              </label>
              <input
                type="text"
                autoFocus
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="• • • • • •"
                required
                className="w-full text-center tracking-[0.5em] font-mono text-xl font-bold py-3 px-4 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || verificationCode.length < 6}
              className="w-full py-3 px-4 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <span>{isSubmitting ? 'Verifying Code...' : 'Verify Email & Sign In'}</span>
              <CheckCircle2 className="w-4 h-4" />
            </button>

            <div className="flex items-center justify-between pt-2 text-xs">
              <button
                type="button"
                onClick={() => {
                  setIsVerifying(false);
                  setError(null);
                  setSuccessMsg(null);
                }}
                className="text-surface-500 hover:text-surface-800 dark:hover:text-surface-200 flex items-center space-x-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to Login</span>
              </button>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
                className="text-brand-600 dark:text-brand-400 font-semibold hover:underline flex items-center space-x-1 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isResending ? 'animate-spin' : ''}`} />
                <span>{isResending ? 'Resending...' : 'Resend Code'}</span>
              </button>
            </div>
          </form>
        ) : (
          /* Credentials Form */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1.5">
                Company Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. mohiuddin@ihbdigital.com"
                required
                className="w-full px-3.5 py-2.5 min-h-[44px] rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 text-surface-900 dark:text-surface-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  className="w-full pl-3.5 pr-11 py-2.5 min-h-[44px] rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 text-surface-900 dark:text-surface-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 focus:outline-none transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-2 py-3 px-4 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <span>{isSubmitting ? 'Authenticating...' : 'Sign In to Workspace'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
